/**
 * How many operator-inbox messages are still PENDING — open, and raised today.
 *
 * App-level rather than feature-level for the same reason as
 * `useWebhookFailuresCount`: two features read this one number — the inbox
 * itself (`features/admin`, the badge on the Pending tab) and the dispatch
 * console (`features/dispatch`, the review KPI under "more checks") — and it
 * lived inside one of them. The console could not reach it without a deep
 * cross-feature import, so it ran its own unbounded `status=open` query and
 * reported the whole ledger: a number that only grows, and that no operator can
 * act on today. One hook means the two surfaces cannot disagree about what
 * "pending" means.
 *
 * `size: 1` because only `total` is read.
 *
 * The day bound rides IN THE QUERY KEY — at merchant midnight the key changes
 * (the day is `useToday()`, so the change happens at the store's day roll, not
 * at the next unrelated render)
 * and the count re-reads for the new day instead of serving yesterday's cached
 * answer. It sits beneath the `queryKeys.reviewQueue.all()` prefix that a
 * resolve already invalidates, so clearing a message refreshes every reader
 * without extra choreography.
 */
import { useQuery } from '@tanstack/react-query';
import { useToday } from '@resource-kit/controllers/react';
import { queryKeys } from '@lib/queryKeys';
import { pendingViewParams, reviewQueueAPI } from '@/services/review-queue';

/** 5 seconds — the inbox is operator-actionable, so freshness matters. */
const COUNT_STALE_MS = 5_000;
/** 30s focused-tab refetch; quiet in the background. */
const BACKGROUND_REFETCH_MS = 30_000;

export function usePendingReviewCount() {
  const params = pendingViewParams(useToday());
  return useQuery({
    queryKey: [...queryKeys.reviewQueue.count(), params.filters['created_at']],
    queryFn: () => reviewQueueAPI.list({ ...params, page: 1, size: 1 }),
    staleTime: COUNT_STALE_MS,
    refetchInterval: BACKGROUND_REFETCH_MS,
    refetchIntervalInBackground: false,
  });
}
